import _defineProperty from "/builds/JohnLewisPartnership/digital/services/online-checkout/checkout-ui/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import { useCallback, useDebugValue, useRef } from 'react';
import { createReduxContextHook, useReduxContext as useDefaultReduxContext } from './useReduxContext';
import { ReactReduxContext } from '../components/Context';
import { notInitialized } from '../utils/useSyncExternalStore';
var useSyncExternalStoreWithSelector = notInitialized;
export var initializeUseSelector = function initializeUseSelector(fn) {
  useSyncExternalStoreWithSelector = fn;
};
var refEquality = function refEquality(a, b) {
  return a === b;
};
/**
 * Hook factory, which creates a `useSelector` hook bound to a given context.
 *
 * @param {React.Context} [context=ReactReduxContext] Context passed to your `<Provider>`.
 * @returns {Function} A `useSelector` hook bound to the specified context.
 */

export function createSelectorHook() {
  var context = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ReactReduxContext;
  var useReduxContext = context === ReactReduxContext ? useDefaultReduxContext : createReduxContextHook(context);
  return function useSelector(selector) {
    var equalityFnOrOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var _ref = typeof equalityFnOrOptions === 'function' ? {
        equalityFn: equalityFnOrOptions
      } : equalityFnOrOptions,
      _ref$equalityFn = _ref.equalityFn,
      equalityFn = _ref$equalityFn === void 0 ? refEquality : _ref$equalityFn,
      _ref$stabilityCheck = _ref.stabilityCheck,
      stabilityCheck = _ref$stabilityCheck === void 0 ? undefined : _ref$stabilityCheck,
      _ref$noopCheck = _ref.noopCheck,
      noopCheck = _ref$noopCheck === void 0 ? undefined : _ref$noopCheck;
    if (process.env.NODE_ENV !== 'production') {
      if (!selector) {
        throw new Error("You must pass a selector to useSelector");
      }
      if (typeof selector !== 'function') {
        throw new Error("You must pass a function as a selector to useSelector");
      }
      if (typeof equalityFn !== 'function') {
        throw new Error("You must pass a function as an equality function to useSelector");
      }
    }
    var _useReduxContext = useReduxContext(),
      store = _useReduxContext.store,
      subscription = _useReduxContext.subscription,
      getServerState = _useReduxContext.getServerState,
      globalStabilityCheck = _useReduxContext.stabilityCheck,
      globalNoopCheck = _useReduxContext.noopCheck;
    var firstRun = useRef(true);
    var wrappedSelector = useCallback(_defineProperty({}, selector.name, function (state) {
      var selected = selector(state);
      if (process.env.NODE_ENV !== 'production') {
        var finalStabilityCheck = typeof stabilityCheck === 'undefined' ? globalStabilityCheck : stabilityCheck;
        if (finalStabilityCheck === 'always' || finalStabilityCheck === 'once' && firstRun.current) {
          var toCompare = selector(state);
          if (!equalityFn(selected, toCompare)) {
            var stack = undefined;
            try {
              throw new Error();
            } catch (e) {
              ;
              stack = e.stack;
            }
            console.warn('Selector ' + (selector.name || 'unknown') + ' returned a different result when called with the same parameters. This can lead to unnecessary rerenders.' + '\nSelectors that return a new reference (such as an object or an array) should be memoized: https://redux.js.org/usage/deriving-data-selectors#optimizing-selectors-with-memoization', {
              state: state,
              selected: selected,
              selected2: toCompare,
              stack: stack
            });
          }
        }
        var finalNoopCheck = typeof noopCheck === 'undefined' ? globalNoopCheck : noopCheck;
        if (finalNoopCheck === 'always' || finalNoopCheck === 'once' && firstRun.current) {
          // @ts-ignore
          if (selected === state) {
            var _stack = undefined;
            try {
              throw new Error();
            } catch (e) {
              ;
              _stack = e.stack;
            }
            console.warn('Selector ' + (selector.name || 'unknown') + ' returned the root state when called. This can lead to unnecessary rerenders.' + '\nSelectors that return the entire state are almost certainly a mistake, as they will cause a rerender whenever *anything* in state changes.', {
              stack: _stack
            });
          }
        }
        if (firstRun.current) firstRun.current = false;
      }
      return selected;
    })[selector.name], [selector, globalStabilityCheck, stabilityCheck]);
    var selectedState = useSyncExternalStoreWithSelector(subscription.addNestedSub, store.getState, getServerState || store.getState, wrappedSelector, equalityFn);
    useDebugValue(selectedState);
    return selectedState;
  };
}
/**
 * A hook to access the redux store's state. This hook takes a selector function
 * as an argument. The selector is called with the store state.
 *
 * This hook takes an optional equality comparison function as the second parameter
 * that allows you to customize the way the selected state is compared to determine
 * whether the component needs to be re-rendered.
 *
 * @param {Function} selector the selector function
 * @param {Function=} equalityFn the function that will be used to determine equality
 *
 * @returns {any} the selected state
 *
 * @example
 *
 * import React from 'react'
 * import { useSelector } from 'react-redux'
 *
 * export const CounterComponent = () => {
 *   const counter = useSelector(state => state.counter)
 *   return <div>{counter}</div>
 * }
 */

export var useSelector = /*#__PURE__*/createSelectorHook();